import {
  AccountProvider,
  MissingFieldRoute,
  PartnerType,
  Routes,
  SipFrequency,
  StorageKey,
  UserEmploymentStatus,
  UserQuery,
} from "@ctypes";
import { i18n, Translations } from "@i18n";

export const getRecurringTypeLabel = (recurringDepositType: SipFrequency) => {
  if (recurringDepositType === SipFrequency.Annually) {
    return i18n.t(Translations.ProjectedChartAdjusterAnnual);
  }
  if (recurringDepositType === SipFrequency.Monthly) {
    return i18n.t(Translations.ProjectedChartAdjusterMonthly);
  }
  return i18n.t(Translations.ProjectedChartAdjusterWeekly);
};

export const getMissingFieldRoute = (data?: UserQuery) => {
  const isIntegratedPartner = Boolean(
    (data?.user?.partnerType === PartnerType.IntegrationDirect ||
      data?.user?.partnerType === PartnerType.IntegrationWhitelabel) &&
    data?.user?.partner,
  );

  const isAlpacaProvider = data?.user?.provider === AccountProvider.Alpaca;

  const isEmployedStatus = Boolean(
    data?.user?.userFinancialProfile?.employmentStatus &&
    [UserEmploymentStatus.Employed, UserEmploymentStatus.SelfEmployed].includes(
      data?.user?.userFinancialProfile?.employmentStatus,
    ),
  );

  const missingFields: Array<MissingFieldRoute> = [
    // Phone login on an unknown number registers rather than failing, so an account can reach
    // onboarding holding nothing but a verified phone. Those users run the same intake the
    // get-started journey uses -- welcome, name, preferred name, email -- rather than the older
    // standalone /name and /email routes, so both entry points look identical and match mobile.
    // The phone step falls out on its own: registering by phone marks the number verified.
    {
      condition: !data?.user?.firstName && !localStorage.getItem(StorageKey.HasSeenGetStarted),
      route: Routes.GetStarted,
    },
    {
      condition: !data?.user?.firstName || !data.user.lastName,
      route: Routes.EntryName,
    },
    {
      condition: !data?.user?.email,
      route: Routes.EntryEmail,
    },
    {
      condition: !data?.user?.phoneNumberVerified,
      route: Routes.PhoneNumber,
    },
    {
      condition: !data?.beneficiaries?.length,
      route: Routes.AddBeneficiaryType,
    },
    {
      condition: !data?.user?.userFinancialProfile?.investmentPriority,
      route: Routes.InvestmentPriority,
    },
    {
      condition: !data?.user?.userFinancialProfile?.downturnReaction,
      route: Routes.InvestmentDownturnReaction,
    },
    {
      condition: !data?.user?.dateOfBirth,
      route: Routes.DateOfBirth,
    },
    {
      condition: !data?.user?.userAddress,
      route: Routes.ResidentialAddress,
    },
    {
      condition: !data?.user?.countryOfCitizenship || !data.user.residenceStatus,
      route: Routes.Citizenship,
    },
    {
      condition: !data?.user?.userTaxProfiles?.length,
      route: Routes.Ssn,
    },
    {
      condition:
        !data?.getBankLinks?.length &&
        !isIntegratedPartner &&
        !sessionStorage.getItem("hasSeenSecureAccountIntro"),
      route: Routes.SecureAccountIntro,
    },
    {
      condition: !data?.getBankLinks?.length && !isIntegratedPartner,
      route: Routes.BankLinking,
    },
    {
      condition: !data?.user?.userFinancialProfile?.employmentStatus,
      route: Routes.Employment,
    },
    {
      condition:
        !data?.user?.userFinancialProfile?.employerAddressStreet &&
        isAlpacaProvider &&
        isEmployedStatus,
      route: Routes.EmployerAddress,
    },
    {
      condition:
        (!data?.user?.userFinancialProfile?.liquidNetWorth ||
          !data?.user?.userFinancialProfile?.annualIncome ||
          !data?.user?.userFinancialProfile?.sourceOfFunds) &&
        isAlpacaProvider,
      route: Routes.AssetsRange,
    },
    {
      condition: !data?.user?.maritalStatus,
      route: Routes.MaritalStatus,
    },
  ];

  return missingFields.find((item) => item.condition)?.route;
};
